import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiHttp, SCHEMA_VERSION } from '@core/api/api-http';
import { deepCopy, mapNull, objectWithoutKey } from '@core/prelude';
import { OpportunityType } from '@shared/models/opportunity';
import { Observable, ReplaySubject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { requiredForMode } from '../models/opportunity-property-definition';
import { environment } from '@env/environment';
import { OpportunityMissingRequirements } from '../modules/core/services/requirement.service';
import { OpportunityRequirement } from '../modules/core/models/schema-property-definition';

function isValueRequirement(
  x: OpportunityMissingRequirements | OpportunityRequirement.ValueRequirement
): x is OpportunityRequirement.ValueRequirement {
  return x.hasOwnProperty('requirementType');
}

function isMissingRequirements(
  x: OpportunityMissingRequirements | OpportunityRequirement.ValueRequirement
): x is OpportunityMissingRequirements {
  return !isValueRequirement(x);
}

export function hasMissingRequirements(
  mode: OpportunityRequirement.Mode,
  opportunityType: OpportunityType,
  requirements: OpportunityMissingRequirements,
  path: string
): boolean {
  const r:
    | OpportunityRequirement.ValueRequirement
    | OpportunityMissingRequirements
    | null = mapNull(
    (x) => selectRequirementsPath(path.split('/'), x),
    requirements
  );
  if (r == null || typeof r == 'string') {
    return false;
  } else if (isValueRequirement(r)) {
    return requiredForMode(r, mode, opportunityType);
  }

  return collectMissingRequirements(r, opportunityType, []).some((x) =>
    requiredForMode(x, mode, opportunityType)
  );
}

function selectRequirementsPath(
  path: string[],
  requirements: OpportunityMissingRequirements
):
  | OpportunityRequirement.ValueRequirement
  | OpportunityMissingRequirements
  | null {
  if (path.length == 0) return requirements;
  if (!(path[0] in requirements)) return null;
  const selection = requirements[path[0]];
  return isMissingRequirements(selection)
    ? selectRequirementsPath(path.slice(1), selection)
    : selection;
}

function collectMissingRequirements(
  requirements: OpportunityMissingRequirements,
  opportunityType: OpportunityType,
  ignoreList: String[]
): OpportunityRequirement.ValueRequirement[] {
  function appendMissingRequirements(
    r: OpportunityMissingRequirements,
    rs: OpportunityRequirement.ValueRequirement[]
  ): OpportunityRequirement.ValueRequirement[] {
    for (let k in r) {
      // An additional check is introduced here to exclude certain requirements that are child items on missing requirements object.
      // For example, discussionNotes under customerNeeds is a structure with fundamental requirement in schema, however,
      // it is not required for PAPP generation - hence ignoring requirements which do not belong to ignore list
      if (!ignoreList.includes(k)) {
        const x:
          | OpportunityRequirement.ValueRequirement
          | OpportunityMissingRequirements = r[k];
        if (isMissingRequirements(x)) {
          rs.concat(appendMissingRequirements(x, rs));
        } else if (isValueRequirement(x)) {
          if (
            x.requirementType == 'Fundamental' ||
            x.requirements.find(
              (requirement) => requirement.opportunityType == opportunityType
            )
          ) {
            rs.push(x);
          }
        } else {
          rs.push(x);
        }
      }
    }
    return rs;
  }

  return appendMissingRequirements(requirements, []);
}

/**
 * @deprecated use `RequirementService`.
 * Please note we need to migrate all the functions meaningfully before deleting this file.
 */
@Injectable({
  providedIn: 'root'
})
export class RequirementModeService {
  private modeSubject: ReplaySubject<OpportunityRequirement.Mode> =
    new ReplaySubject<OpportunityRequirement.Mode>(1);
  private _currentMode: OpportunityRequirement.Mode | null = null;
  private _opportunityType!: OpportunityType;
  private requirementsSource: ReplaySubject<OpportunityMissingRequirements> =
    new ReplaySubject<OpportunityMissingRequirements>(1);
  private missingRequirements: OpportunityMissingRequirements | null = null;

  public mode: Observable<OpportunityRequirement.Mode> = this.modeSubject;
  public requirements: Observable<OpportunityMissingRequirements> =
    this.requirementsSource;

  constructor(private http: ApiHttp) {}

  updateMode(mode: OpportunityRequirement.Mode): void {
    this.modeSubject.next(mode);
    this._currentMode = mode;
  }

  updateRequirements(
    requirements: OpportunityMissingRequirements,
    opportunityType: OpportunityType
  ): void {
    this._opportunityType = opportunityType;
    this.requirementsSource.next(requirements);
    this.missingRequirements = requirements;
  }

  switchMode(
    opportunityId: string,
    mode: OpportunityRequirement.Mode
  ): Observable<object> {
    let options = {
      headers: new HttpHeaders({
        'Content-Type': 'text/plain'
      })
    };
    return this.http
      .put(
        environment.opportunityApiUrl,
        `opportunities/${opportunityId}/requirement-mode`,
        mode,
        SCHEMA_VERSION.OPPORTUNITY,
        options
      )
      .pipe(tap((_) => this.updateMode(mode)));
  }

  hasMissingRequirements(path: string): boolean {
    return this._currentMode !== null && this.missingRequirements !== null
      ? hasMissingRequirements(
          this._currentMode,
          this._opportunityType,
          this.missingRequirements,
          path
        )
      : false;
  }

  public hasMissingProductRecommendationRequirements(): boolean {
    if (this.missingRequirements) {
      let requirements = deepCopy(
        this.missingRequirements
      ) as OpportunityMissingRequirements;
      requirements = objectWithoutKey(
        requirements,
        'submission'
      ) as OpportunityMissingRequirements;
      return (
        collectMissingRequirements(requirements, this._opportunityType, [
          'discussionNotes'
        ]).filter((r) =>
          requiredForMode(
            r,
            OpportunityRequirement.Mode.Recommendation,
            this._opportunityType
          )
        ).length > 0
      );
    }
    return false;
  }

  public isApplicationMode(): boolean {
    return this._currentMode === 'Application';
  }

  public isRecommendationMode(): boolean {
    return this._currentMode === 'Recommendation';
  }
}
